import type {Plugin} from 'unified'

import {extractChatCommand} from '../chat-commands-helpers'
import {referenceID} from '../copilot-chat-helpers'
import type {CopilotChatCommandType, CopilotChatReference} from '../copilot-chat-types'
import {copilotFeatureFlags} from '../copilot-feature-flags'
import {ReferenceMention} from '../reference-mention'
import type {UserMessageNode, UserMessageTree} from './user-message-tree'
import {extractCodeContent, isCodeFenceSegment, splitCodeFences} from './user-message-utils'

interface ParseUserMessageOptions {
  /**
   * All references that are valid to mention in this message. If a `reference-mention` node matches one of these
   * references, it will have the corresponding `mentionedReferenceId` data property set on it.
   */
  references: CopilotChatReference[]
  /**
   * Plugins to extract available commands from
   */
  commands: CopilotChatCommandType[]
  rawFences?: boolean
}

/**
 * Unified.js plugin that parses a user's message into an abstract syntax tree.
 */
// eslint-disable-next-line func-style
export const userMessageParser: Plugin<[ParseUserMessageOptions], string, UserMessageTree> = function (
  this,
  {references, commands, rawFences = false},
) {
  this.parser = function (text: string) {
    const nodes: UserMessageNode[] = []

    if (copilotFeatureFlags.prettifyPastedCode) {
      let isFirstTextSegment = true
      for (const segment of splitCodeFences(text)) {
        if (isCodeFenceSegment(segment)) {
          nodes.push(
            rawFences ? {type: 'text', value: segment} : {type: 'code-block', value: extractCodeContent(segment)},
          )
        } else {
          parseTextSegment(segment, nodes, {references, commands}, isFirstTextSegment)
          isFirstTextSegment = false
        }
      }
    } else {
      parseTextSegment(text, nodes, {references, commands})
    }

    return {
      type: 'root',
      children: nodes,
    }
  }
}

function parseTextSegment(
  text: string,
  nodes: UserMessageNode[],
  {references, commands}: ParseUserMessageOptions,
  isFirstSegment = true,
) {
  // whitespace must be preserved so the preview stays visually aligned with the textarea.
  const tokens = text.split(/(\s+)/) // splitting with a regex includes all the whitespace as tokens
  const firstNonWhitespaceIndex = tokens.findIndex(t => t.trim() !== '')

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i] ?? ''
    // Check for commands at the start of the message
    if (copilotFeatureFlags.chatInputCommands && isFirstSegment && i === firstNonWhitespaceIndex) {
      // Join remaining tokens to match compound commands (e.g. "/chronicle standup")
      const remainingText = tokens.slice(i).join('')
      const commandResult = extractChatCommand(remainingText, commands)

      if (commandResult) {
        nodes.push({
          type: 'command',
          value: commandResult.matchedText,
          data: {command: commandResult.command},
        })

        // Skip tokens consumed by the matched command
        let consumed = 0
        while (i < tokens.length && consumed < commandResult.matchedText.length) {
          consumed += (tokens[i] ?? '').length
          i++
        }
        i-- // compensate for loop increment
        continue
      }
    }

    const referenceMention = ReferenceMention.parse(token)
    const mentionedReference = referenceMention && references?.find(r => ReferenceMention.refersTo(referenceMention, r))

    if (referenceMention) {
      nodes.push({
        type: 'reference-mention',
        value: token,
        data: {
          mentionedReferenceId: mentionedReference && referenceID(mentionedReference),
          referenceMention,
        },
      })
      continue
    }

    // Combine adjacent text nodes instead of having a text node for every word
    const lastNode = nodes.at(-1)
    if (lastNode?.type === 'text') lastNode.value += token
    else nodes.push({type: 'text', value: token})
  }
}
